import React, { useRef } from 'react';
import { isEmpty, noop } from 'lodash';
import { Stack } from '@fiverr-private/layout_components';
import { OnTagsEvent, ShowMoreTagsItemProps, Tag, TagItemProps, TagsLayout } from '../../types';
import { useParentWidth } from '../../hooks/useParentWidth';
import { DEFAULT_TAGS_LAYOUT } from './TagCollection.config';
import { TagsWrapper } from './TagsWrapper';
import { TagsContextProvider } from './TagsContext/TagsContextProvider';
import { DefaultTagItem } from './TagItems/DefaultTagItem';
import { DefaultShowMoreTagsItemProps } from './TagItems/DefaultShowMoreTagsItem';

const TAGS_TO_SHOW_LIMIT = 10;
const DEFAULT_NUM_OF_LINES = 2;

interface TagsProps {
    parentRef: React.RefObject<HTMLElement>;
    tagsLayout?: TagsLayout;
    onViewTags?: OnTagsEvent;
    onShowMoreTags?: OnTagsEvent;
    tags?: Tag[];
    numOfLines?: number;
    tagsToShowLimit?: number;
    linesOffsets?: number[];
    TagItem?: React.FC<TagItemProps>;
    ShowMoreTagsItem?: React.FC<ShowMoreTagsItemProps>;
    shouldStoreEmptySpace?: boolean;
}

export const TagCollection: React.FC<TagsProps> = ({
    parentRef,
    tagsLayout = DEFAULT_TAGS_LAYOUT,
    onViewTags = noop,
    onShowMoreTags = noop,
    tags = [],
    numOfLines = DEFAULT_NUM_OF_LINES,
    tagsToShowLimit = TAGS_TO_SHOW_LIMIT,
    linesOffsets,
    TagItem = DefaultTagItem,
    ShowMoreTagsItem = DefaultShowMoreTagsItemProps,
    shouldStoreEmptySpace = true,
}) => {
    const ref = useRef<HTMLElement>(null);
    const { parentWidth, containerHeight, enrichedTagsLayout } = useParentWidth({
        parentRef,
        tagsLayout,
        numOfLines,
    });

    if (isEmpty(tags)) {
        return null;
    }

    if (ref.current && shouldStoreEmptySpace) {
        ref.current.style.minHeight = `${containerHeight}px`;
    }

    return (
        <TagsContextProvider
            tags={tags}
            tagsLayout={enrichedTagsLayout}
            numOfLines={numOfLines}
            parentWidth={parentWidth}
            linesOffsets={linesOffsets}
            tagsToShowLimit={tagsToShowLimit}
            onViewTags={onViewTags}
            onShowMoreTags={onShowMoreTags}
            TagItem={TagItem}
            ShowMoreTagsItem={ShowMoreTagsItem}
        >
            <Stack gap="4" direction="column" ref={ref}>
                <TagsWrapper />
            </Stack>
        </TagsContextProvider>
    );
};
